'use client';

import Link, { type LinkProps } from 'next/link';
import {
  useRef,
  type ReactNode,
  type MouseEvent,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
} from 'react';

type Shared = { className?: string; children?: ReactNode };

type MagneticButtonProps =
  | ({ as: 'link' } & LinkProps & Shared & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps>)
  | ({ as?: 'a' } & AnchorHTMLAttributes<HTMLAnchorElement> & Shared)
  | ({ as: 'button' } & ButtonHTMLAttributes<HTMLButtonElement> & Shared)
  | ({ as: 'div' } & HTMLAttributes<HTMLDivElement> & Shared);

/**
 * Ported as-is from scripts.js banner 12 — mousemove/mouseleave transform
 * offset, factor 0.15. Takes a string `as` discriminator (not a component
 * reference) so it can be used from Server Component pages without
 * crossing a function prop over the server/client boundary.
 */
export default function MagneticButton(props: MagneticButtonProps) {
  const ref = useRef<HTMLAnchorElement & HTMLButtonElement & HTMLDivElement>(null);

  const handleMouseMove = (e: MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    el.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
  };

  const handleMouseLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'translate(0, 0)';
  };

  if (props.as === 'button') {
    const { as, ...rest } = props; void as;
    return <button ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} {...rest} />;
  }

  if (props.as === 'div') {
    const { as, ...rest } = props; void as;
    return <div ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} {...rest} />;
  }

  if (props.as === 'link') {
    const { as, ...rest } = props; void as;
    return <Link ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} {...rest} />;
  }

  const { as, ...rest } = props; void as;
  return <a ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} {...rest} />;
}
